import { Injectable, signal, computed } from '@angular/core';
import { Language, LanguageOption, AVAILABLE_LANGUAGES, TRANSLATIONS } from './translations';

@Injectable({
  providedIn: 'root',
})
export class I18nService {
  readonly availableLanguages: LanguageOption[] = AVAILABLE_LANGUAGES;

  readonly currentLanguage = signal<Language>(this.getInitialLanguage());

  readonly currentLanguageOption = computed(() => {
    const code = this.currentLanguage();
    return this.availableLanguages.find((l) => l.code === code) || this.availableLanguages[0];
  });

  private getInitialLanguage(): Language {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const stored = localStorage.getItem('fm_language') as Language;
        if (stored && ['es', 'ca', 'gl', 'eu'].includes(stored)) {
          return stored;
        }
        // Check browser language
        const navLang = navigator.language?.toLowerCase() || '';
        if (navLang.startsWith('ca')) return 'ca';
        if (navLang.startsWith('gl')) return 'gl';
        if (navLang.startsWith('eu')) return 'eu';
      }
    } catch {
      // ignore
    }
    return 'es';
  }

  setLanguage(lang: Language): void {
    if (['es', 'ca', 'gl', 'eu'].includes(lang)) {
      this.currentLanguage.set(lang);
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          localStorage.setItem('fm_language', lang);
          document.documentElement.lang = lang;
        }
      } catch {
        // ignore
      }
    }
  }

  /**
   * Translate literal key with optional interpolations
   */
  t(key: string, params?: Record<string, string | number>): string {
    const lang = this.currentLanguage();
    const dict = TRANSLATIONS[lang] || TRANSLATIONS['es'];
    let text = dict[key] ?? TRANSLATIONS['es']?.[key] ?? key;

    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        text = text.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
      });
    }

    return text;
  }
}
